import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { type MatchResultDto, type RealtimeEvent, type RoomDto, roomTopic } from '@chromarble/shared-types';
import { ErrorNote, Screen, ShareSheet, formatScore } from '../components/ui';
import { useRealtimeTopic } from '../hooks/realtime';
import { api } from '../services/api';
import { hasCrewReturn, returnToCrew } from '../services/crew-bridge';
import { useSession } from '../services/session';

const OUTCOME: Record<string, string> = { WIN: '승리', DRAW: '공동 우승', LOSS: '패배' };

export default function ResultPage() {
  const { matchId = '' } = useParams();
  const navigate = useNavigate();
  const user = useSession((s) => s.user)!;
  const [result, setResult] = useState<MatchResultDto | null>(null);
  const [room, setRoom] = useState<RoomDto | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [rematchNotice, setRematchNotice] = useState<{ roomId: string; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = (await api.result(matchId)).result;
      setResult(r);
      api.room(r.roomId).then((x) => setRoom(x.room), () => setRoom(null));
      return r.finalized;
    } catch (e) {
      setError(e);
      return true;
    }
  }, [matchId]);

  // Poll until the server has finalized the official result.
  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      const done = await load();
      if (!done && !stop) timer = setTimeout(tick, 1000);
    };
    void tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [load]);

  const isMember = Boolean(room?.members.some((m) => m.userId === user.id));
  useRealtimeTopic(isMember && result ? roomTopic(result.roomId) : null, (e: RealtimeEvent) => {
    if (e.type === 'MATCH_FINISHED' && e.matchId === matchId) setResult(e.result);
    if (e.type === 'ROOM_REMATCH') {
      if (e.kind === 'FULL') setRematchNotice({ roomId: e.targetRoomId, text: '방장이 재대전을 열었습니다.' });
      else if (e.userIds.includes(user.id)) setRematchNotice({ roomId: e.targetRoomId, text: '동점자 재대전 방이 열렸습니다.' });
    }
  });

  if (!result) {
    return (
      <Screen title="경기 결과" back="/">
        {error ? <ErrorNote error={error} /> : <p className="muted">결과를 불러오는 중…</p>}
      </Screen>
    );
  }

  const mine = result.standings.find((s) => s.userId === user.id);
  const nick = new Map(result.standings.map((s) => [s.userId, s.nickname]));
  const winners = result.winnerIds.map((id) => nick.get(id)).join(', ');
  const inTiebreak = result.tiebreak?.userIds.includes(user.id);
  const canFullRematch = room && room.allowRematch && room.status === 'FINISHED' && room.currentMatchId === matchId && room.hostUserId === user.id;

  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen title="경기 결과" back="/">
      {!result.finalized && (
        <p className="notice" data-testid="result-pending">
          서버에서 결과를 확정하는 중입니다… (아래 순위는 임시)
        </p>
      )}
      <section className="card" data-testid="result" data-finalized={result.finalized}>
        <p className="muted small">{result.roomTitle}</p>
        <h2 data-testid="winner">
          {result.winnerIds.length === 0 ? '우승자 없음' : result.coWinners ? `공동 우승: ${winners}` : `우승: ${winners}`}
        </h2>
        <ol className="standings" data-testid="standings">
          {result.standings.map((s) => (
            <li key={s.userId} className={s.userId === user.id ? 'me' : ''} data-testid="standing-row" data-user={s.userId}>
              <span className="rank">{s.finalRank}위</span>
              <span className="name">
                {s.nickname}
                {s.finishStatus === 'FORFEITED' && <span className="badge">기권</span>}
              </span>
              <span className="score">{formatScore(s.finalScore)}점</span>
            </li>
          ))}
        </ol>
        {mine && (
          <p data-testid="my-result">
            내 성적: <strong>{mine.finalRank}위</strong> · {formatScore(mine.finalScore)}점 · {OUTCOME[mine.outcome]}
          </p>
        )}
        {result.lunch && (
          <p className="lunch" data-testid="lunch-result">
            점심내기 결과:{' '}
            {result.lunch.payerUserId ? (
              <strong>{nick.get(result.lunch.payerUserId)}</strong>
            ) : result.lunch.tie ? (
              <>최하위 동점 ({result.lunch.tiedUserIds.map((id) => nick.get(id)).join(', ')}) — 재대전으로 결정</>
            ) : (
              '-'
            )}
            <br />
            <span className="muted small">실제 결제는 게임에서 처리하지 않습니다.</span>
          </p>
        )}
        {result.rematchOfMatchId && <p className="muted small">이전 경기와 연결된 재대전 결과입니다.</p>}
      </section>

      {rematchNotice && (
        <div className="notice">
          {rematchNotice.text}{' '}
          <button className="btn small-btn" onClick={() => navigate(`/rooms/${rematchNotice.roomId}`)}>
            게임방으로
          </button>
        </div>
      )}

      {result.finalized && (
        <div className="stack">
          {canFullRematch && (
            <button
              className="btn"
              disabled={busy}
              data-testid="rematch"
              onClick={() =>
                act(async () => {
                  const r = await api.rematch(result.roomId, 'FULL');
                  navigate(`/rooms/${r.room.id}`);
                })
              }
            >
              재대전
            </button>
          )}
          {room && room.status === 'WAITING' && room.currentMatchId === matchId && isMember && (
            <button className="btn" onClick={() => navigate(`/rooms/${room.id}`)}>
              재대전 로비로
            </button>
          )}
          {result.tiebreak && inTiebreak && (
            <button
              className="btn"
              disabled={busy}
              data-testid="tiebreak"
              onClick={() =>
                act(async () => {
                  const r = await api.rematch(result.roomId, result.tiebreak!.kind);
                  navigate(`/rooms/${r.room.id}`);
                })
              }
            >
              {result.tiebreak.kind === 'TIEBREAK_LAST' ? '최하위 동점자 재대전' : '공동 우승자 결정전'}
            </button>
          )}
          {mine && (
            <ShareSheet
              load={() => api.shareResult(matchId)}
              scopeNote="이 경기의 참가자 닉네임·점수·순위가 포함됩니다. 복사한 메시지를 붙여넣는 Crew 채팅방 구성원에게 보입니다."
            />
          )}
        </div>
      )}
      <ErrorNote error={error} />
      <div className="row">
        <button className="btn secondary" onClick={() => navigate('/')}>
          나가기
        </button>
        {hasCrewReturn() && (
          <button className="btn secondary" onClick={returnToCrew}>
            Crew로 돌아가기
          </button>
        )}
      </div>
    </Screen>
  );
}
